import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Typography, Box, Paper, TextField, Button,
    Alert, Select, MenuItem, FormControl, InputLabel, CircularProgress,
    IconButton, Chip, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Collapse, Stack, InputAdornment,
    useTheme, useMediaQuery, Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TerminalIcon from '@mui/icons-material/Terminal';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const getLevelChipColor = (level) => {
    switch (level?.toUpperCase()) {
        case 'ERROR':
            return { color: 'error', bg: '#ffebee', textColor: '#c62828' };
        case 'CRITICAL':
            return { color: 'error', bg: '#fbe9e7', textColor: '#b71c1c' };
        case 'WARNING':
            return { color: 'warning', bg: '#fff3e0', textColor: '#e65100' };
        case 'INFO':
            return { color: 'info', bg: '#e3f2fd', textColor: '#1565c0' };
        case 'DEBUG':
            return { color: 'default', bg: '#f5f5f5', textColor: '#616161' };
        default:
            return { color: 'default', bg: '#f5f5f5', textColor: '#616161' };
    }
};

const LogCard = React.memo(({ entry }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const hasStackTrace = entry.message.includes('\n');
    const chipStyle = getLevelChipColor(entry.level);

    return (
        <Paper
            elevation={0}
            onClick={() => setOpen(!open)}
            sx={{
                p: 2,
                mb: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                cursor: 'pointer'
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Chip
                    label={entry.level}
                    size="small"
                    sx={{
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        bgcolor: chipStyle.bg,
                        color: chipStyle.textColor,
                        borderRadius: 1,
                        height: 22
                    }}
                />
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                    {entry.timestamp || 'N/A'}
                </Typography>
            </Box>

            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'primary.main', display: 'block', mb: 0.5, fontWeight: 600 }}>
                {entry.logger}
            </Typography>

            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                {entry.message.split('\n')[0]}
                {hasStackTrace && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main', fontWeight: 600 }}>
                        (+ stacktrace)
                    </Typography>
                )}
            </Typography>

            <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 1.5 }}>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                        {t('FULL LOG DETAIL')}
                    </Typography>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 1.5,
                            bgcolor: '#1e1e1e',
                            color: '#d4d4d4',
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            borderRadius: 1,
                            overflowX: 'auto'
                        }}
                    >
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                            {entry.raw}
                        </pre>
                    </Paper>
                </Box>
            </Collapse>
        </Paper>
    );
});

const LogRow = ({ entry }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const hasStackTrace = entry.message.includes('\n');
    const chipStyle = getLevelChipColor(entry.level);

    return (
        <>
            <TableRow
                hover
                onClick={() => setOpen(!open)}
                sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
            >
                <TableCell style={{ width: 48 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'nowrap', fontWeight: 500 }}>
                    {entry.timestamp || 'N/A'}
                </TableCell>
                <TableCell sx={{ width: 110 }}>
                    <Chip
                        label={entry.level}
                        size="small"
                        sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            bgcolor: chipStyle.bg,
                            color: chipStyle.textColor,
                            borderRadius: 1
                        }}
                    />
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'text.secondary', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.logger}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                    {entry.message.split('\n')[0]}
                    {hasStackTrace && (
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main', fontWeight: 600 }}>
                            (+ stacktrace)
                        </Typography>
                    )}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, my: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                                {t('FULL LOG DETAIL')}
                            </Typography>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    bgcolor: '#1e1e1e',
                                    color: '#d4d4d4',
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem',
                                    borderRadius: 1,
                                    overflowX: 'auto'
                                }}
                            >
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                    {entry.raw}
                                </pre>
                            </Paper>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const AdminLogs = () => {
    const { t } = useTranslation();
    const { currentUser, api } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [logs, setLogs] = useState([]);
    const [totalLogs, setTotalLogs] = useState(0);
    const [selectedLevel, setSelectedLevel] = useState('ALL');
    const [searchQuery, setSearchQuery] = useState('');
    const [limit, setLimit] = useState(250);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const LOG_LEVELS = [
        { label: t('All Levels'), value: 'ALL' },
        { label: 'ERROR', value: 'ERROR' },
        { label: 'WARNING', value: 'WARNING' },
        { label: 'INFO', value: 'INFO' },
        { label: 'DEBUG', value: 'DEBUG' },
        { label: 'CRITICAL', value: 'CRITICAL' },
    ];

    const fetchLogs = useCallback(async (isManualRefresh = false) => {
        if (isManualRefresh) setRefreshing(true);
        setError(null);
        try {
            const params = {
                level: selectedLevel,
                limit: limit
            };
            if (searchQuery.trim()) {
                params.search = searchQuery.trim();
            }
            const response = await api.get('/admin/logs', { params });
            setLogs(response.data.entries);
            setTotalLogs(response.data.total);
        } catch (err) {
            console.error("Failed to fetch server logs", err);
            setError(err.response?.data?.detail || t("Could not load server logs."));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [api, selectedLevel, searchQuery, limit, t]);

    useEffect(() => {
        if (currentUser && !currentUser.is_super_admin) {
            navigate('/');
            return;
        }

        if (currentUser?.is_super_admin) {
            fetchLogs();
        }
    }, [currentUser, fetchLogs, navigate]);

    if (!currentUser?.is_super_admin) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">{t('You do not have permission to access this page.')}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 4,
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => navigate('/admin')} sx={{ mr: { xs: 1, sm: 2 } }} color="primary">
                        <ArrowBackIcon />
                    </IconButton>
                    <TerminalIcon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                            {t('Server Logs')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('View and filter server application logs sorted by date (newest first)')}
                        </Typography>
                    </Box>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
                    onClick={() => fetchLogs(true)}
                    disabled={refreshing || loading}
                    sx={{ borderRadius: 2 }}
                >
                    {t('Refresh')}
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {/* Filter Controls Bar */}
            <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 160, width: { xs: '100%', md: 'auto' } }}>
                        <InputLabel id="log-level-label">{t('Log Level')}</InputLabel>
                        <Select
                            labelId="log-level-label"
                            value={selectedLevel}
                            label={t('Log Level')}
                            onChange={(e) => setSelectedLevel(e.target.value)}
                        >
                            {LOG_LEVELS.map(lvl => (
                                <MenuItem key={lvl.value} value={lvl.value}>
                                    {lvl.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        placeholder={t('Search logs by keyword...')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ flexGrow: 1, width: { xs: '100%', md: 'auto' } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <FormControl size="small" sx={{ minWidth: 120, width: { xs: '100%', md: 'auto' } }}>
                        <InputLabel id="limit-label">{t('Max Lines')}</InputLabel>
                        <Select
                            labelId="limit-label"
                            value={limit}
                            label={t('Max Lines')}
                            onChange={(e) => setLimit(e.target.value)}
                        >
                            <MenuItem value={50}>50 {t('lines')}</MenuItem>
                            <MenuItem value={100}>100 {t('lines')}</MenuItem>
                            <MenuItem value={250}>250 {t('lines')}</MenuItem>
                            <MenuItem value={500}>500 {t('lines')}</MenuItem>
                            <MenuItem value={1000}>1000 {t('lines')}</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </Paper>

            {/* Logs Table */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : logs.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {t('No log entries found matching the current filters.')}
                    </Typography>
                </Paper>
            ) : isMobile ? (
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ px: 1, py: 1, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {t('Showing {{count}} of {{total}} log entries', { count: logs.length, total: totalLogs })}
                        </Typography>
                    </Box>
                    {logs.map((entry) => (
                        <LogCard key={entry.id} entry={entry} />
                    ))}
                </Box>
            ) : (
                <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
                    <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {t('Showing {{count}} of {{total}} log entries (Sorted Newest First)', { count: logs.length, total: totalLogs })}
                        </Typography>
                    </Box>
                    <TableContainer sx={{ maxHeight: 650 }}>
                        <Table stickyHeader size="small" aria-label="server logs table">
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ width: 48 }} />
                                    <TableCell sx={{ fontWeight: 700 }}>{t('Timestamp')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, width: 110 }}>{t('Level')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('Logger')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('Message')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.map((entry) => (
                                    <LogRow key={entry.id} entry={entry} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Container>
    );
};

export default AdminLogs;
